import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BasicUser } from '../../../models/identity/basic-user.model';
import { OrganizationService } from '../../../services/identity/organization.service';
import { ImageService } from '../../../services/shared/image.service';

@Component({
    selector: 'app-organization-members-modal',
    template: `
        <p-dialog
            [(visible)]="visible"
            [style]="{ width: '90%', maxWidth: '800px' }"
            [header]="'organizations.memberModal.collaborators' | translate"
            [modal]="true"
            [draggable]="false"
            [resizable]="false"
            [closeOnEscape]="true"
            [dismissableMask]="true"
            [appendTo]="'body'"
        >
            <div class="dialog-content">
                <div *ngIf="isLoadingEmployees" class="text-center my-4">
                    <wudo-spinner></wudo-spinner>
                </div>
                <ul class="list-group list-participants" *ngIf="!isLoadingEmployees && employees?.length > 0; else noEmployees">
                    <li *ngFor="let profile of employees" class="list-group-item">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center">
                                <div class="col-auto">
                                    <app-user-avatar
                                        [firstName]="profile.firstName"
                                        [lastName]="profile.lastName"
                                        [picture]="profile.pictureAccessLink || profile.pictureReference"
                                        [routerLink]="['/directory/profile', profile.userId]"
                                    >
                                    </app-user-avatar>
                                </div>
                                <div class="d-flex flex-column ms-2">
                                    <div>{{ profile.firstName }} {{ profile.lastName }}</div>
                                    <div>
                                        <span class="text-muted">{{ profile.jobTitle }}</span>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <a [routerLink]="['/directory/profile', profile.userId]" class="btn btn-primary btn-sm" (click)="hideDialog()">
                                    <em class="icon icon-preview d-sm-inline d-md-none"></em>
                                    <span class="d-none d-md-inline">{{ 'organizations.memberModal.seeProfil' | translate }}</span>
                                </a>
                            </div>
                        </div>
                    </li>
                </ul>
                <ng-template #noEmployees>
                    <p class="text-center text-muted" *ngIf="!isLoadingEmployees">{{ 'organizations.memberModal.noActiveEmployees' | translate }}</p>
                </ng-template>
            </div>

            <ng-template pTemplate="footer">
                <button type="button" class="btn btn-secondary" (click)="hideDialog()">
                    {{ 'components.button.buttonClose' | translate }}
                </button>
            </ng-template>
        </p-dialog>
    `,
    styles: [
        `
            :host ::ng-deep .p-dialog-header {
                padding: 1rem;
                border-bottom: 1px solid #e9ecef;
            }
            :host ::ng-deep .p-dialog-content {
                padding: 0;
            }
            :host ::ng-deep .p-dialog-footer {
                padding: 1rem;
                border-top: 1px solid #e9ecef;
                text-align: right;
            }
            .dialog-content {
                padding: 1rem;
            }
        `,
    ],
})
export class OrganizationMembersModalComponent implements OnDestroy {
    @Input() organizationId: string;
    visible = false;
    protected destroy$ = new Subject<void>();
    public employees: BasicUser[] = [];
    public isLoadingEmployees = false;
    constructor(
        public imageService: ImageService,
        private translate: TranslateService,
        private readonly organizationService: OrganizationService
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    showDialog(): void {
        this.visible = true;
        this.loadEmployees();
    }

    hideDialog(): void {
        this.visible = false;
    }

    loadEmployees(): void {
        if (this.organizationId) {
            this.isLoadingEmployees = true;
            this.organizationService
                .getEmployees(this.organizationId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (data) => {
                        this.employees = data;
                        this.isLoadingEmployees = false;
                    },
                    error: (err) => {
                        this.employees = [];
                        this.isLoadingEmployees = false;
                    },
                });
        }
    }
}
